// ============================================================
// Jobs Page - List + Create
// ============================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Plus, ArrowRight, Search, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';

export function JobsPage() {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', location: '', department: '' });

    const { data, isLoading } = useQuery({
        queryKey: ['jobs'],
        queryFn: () => api.get('/jobs'),
    });

    const createMutation = useMutation({
        mutationFn: (body: any) => api.post('/jobs', body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            setShowCreate(false);
            setForm({ title: '', description: '', location: '', department: '' });
            toast.success('Job created');
        },
        onError: (err: any) => toast.error(err.message),
    });

    const jobs = data?.data || [];

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <h1>Jobs</h1>
                    <p className="subtitle">Manage your open positions and recruitment pipeline</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    <Plus size={16} /> New Job
                </button>
            </div>

            {jobs.length === 0 && !isLoading ? (
                <div className="card">
                    <div className="empty-state">
                        <div className="icon">💼</div>
                        <h3>No jobs yet</h3>
                        <p>Create your first job posting to start receiving applications</p>
                        <button className="btn btn-primary mt-4" onClick={() => setShowCreate(true)}>
                            <Plus size={16} /> Create Job
                        </button>
                    </div>
                </div>
            ) : (
                <div className="card-grid card-grid-2">
                    {jobs.map((job: any) => (
                        <div key={job.id} className="card" style={{ cursor: 'pointer' }}>
                            <div className="flex justify-between items-center mb-4">
                                <span className={`badge badge-${job.status === 'active' ? 'success' : job.status === 'draft' ? 'warning' : 'info'}`}>
                                    {job.status}
                                </span>
                                {job.department && <span className="badge badge-accent">{job.department}</span>}
                            </div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>{job.title}</h3>
                            {job.location && <p style={{ color: '#4b5563', fontSize: '0.875rem', marginBottom: 4 }}>📍 {job.location}</p>}
                            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: 16 }}>
                                {job.description?.substring(0, 120) || 'No description'}...
                            </p>
                            <div className="flex gap-2">
                                <Link to={`/jobs/${job.id}`} className="btn btn-secondary btn-sm flex-1">
                                    Manage <ArrowRight size={14} />
                                </Link>
                                <Link to={`/jobs/${job.id}/leaderboard`} className="btn btn-ghost btn-sm">
                                    <BarChart3 size={14} /> Leaderboard
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            {showCreate && (
                <div className="modal-overlay" onClick={() => setShowCreate(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Job</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            createMutation.mutate(form);
                        }}>
                            <div className="form-group">
                                <label className="form-label">Job Title *</label>
                                <input className="form-input" value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder="e.g. Senior Software Engineer" required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Department</label>
                                <input className="form-input" value={form.department}
                                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                                    placeholder="e.g. Engineering" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Location</label>
                                <input className="form-input" value={form.location}
                                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                                    placeholder="e.g. Bangalore, India" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Description</label>
                                <textarea className="form-textarea" value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder="Job description..." rows={4} />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary flex-1" disabled={createMutation.isPending}>
                                    {createMutation.isPending ? 'Creating...' : 'Create Job'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
